import { SearchIcon } from "@xola/icons";
import { Input } from "@xola/ui-kit";
import clsx from "clsx";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useSearchAutoFocus } from "@/hooks/useSearchAutoFocus";

interface SearchInputProps {
    className?: string;
    classNames?: { input?: string };
    value?: string;
    id?: string;
    placeholder?: string;
    onChange: (searchString: string) => void;
    rightAdornment?: React.ReactNode;
    size?: string;
    shouldFocus?: boolean;
}

export const SearchInput = ({
    className,
    classNames,
    value,
    id,
    placeholder = "Search",
    onChange,
    rightAdornment,
    size = "small",
    shouldFocus = false,
    ...rest
}: SearchInputProps) => {
    const autoFocusEnabled = useSearchAutoFocus();
    const [localValue, setLocalValue] = useState(value);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        onChange(inputValue);
        setLocalValue(inputValue);
    };

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (shouldFocus && autoFocusEnabled) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [inputRef, shouldFocus, autoFocusEnabled]);

    return (
        <div className={clsx("relative flex items-center", className)}>
            <Input
                ref={inputRef}
                id={id}
                type="search"
                placeholder={placeholder}
                className={clsx("pl-8", { "pr-20": rightAdornment }, classNames?.input)}
                value={localValue}
                onChange={handleInputChange}
                {...rest}
            />

            <div className="absolute left-3 top-1/2 -translate-y-1/2 transform">
                <SearchIcon className="text-gray-400" size={size} />
            </div>

            {rightAdornment ? (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 transform">{rightAdornment}</div>
            ) : null}
        </div>
    );
};
